import { Link } from "react-router-dom";
import { Building2, Check, Users, MapPin, Monitor, Percent, DollarSign, TrendingUp, Gift } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { BEST_FOR_LABELS, BROKERAGE_TYPE_LABELS, formatCurrency, formatSplit } from "@/contracts/brokerage";

interface BrokerageCardData {
  id?: string;
  slug?: string;
  name?: string;
  logo?: string | null;
  description?: string | null;
  coverage_type?: 'national' | 'regional' | 'local' | null;
  brokerage_size?: string | null;
  brokerage_type?: 'physical' | 'hybrid' | 'virtual' | null;
  support_level?: string | null;
  best_for?: string[];
  is_published?: boolean;
  locations_in_state?: number;
  // Financial fields
  split_starting_percent?: number | null;
  split_max_percent?: number | null;
  cap_amount?: number | null;
  monthly_fee?: number | null;
  transaction_fee?: number | null;
  sign_on_bonus_max?: number | null;
  has_revenue_share?: boolean;
  has_stock_awards?: boolean;
}

interface BrokerageCardProps {
  brokerage: BrokerageCardData;
  distance?: number | null;
  isCompareSelected?: boolean;
  onCompareToggle?: (id: string) => void;
  compareDisabled?: boolean;
}

export function BrokerageCard({
  brokerage,
  distance,
  isCompareSelected = false,
  onCompareToggle,
  compareDisabled = false,
}: BrokerageCardProps) {
  const getCoverageColor = (coverage?: string | null) => {
    switch (coverage) {
      case 'national': return 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-400';
      case 'regional': return 'bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-400';
      case 'local': return 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-400';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getBrokerageTypeIcon = (type?: string | null) => {
    switch (type) {
      case 'virtual': return <Monitor className="h-3 w-3" />;
      default: return <Building2 className="h-3 w-3" />;
    }
  };

  const hasFinancialData = brokerage.split_starting_percent || brokerage.cap_amount || brokerage.monthly_fee;

  return (
    <Link to={`/brokerage/${brokerage.slug}`} className="h-full block">
      <div className="group bg-card rounded-lg overflow-hidden border transition-all hover:shadow-hover h-full flex flex-col">
        {/* Hero image area - matching DealCard aspect-video */}
        <div className="relative aspect-video overflow-hidden bg-secondary flex items-center justify-center">
          {brokerage.logo ? (
            <img
              src={brokerage.logo}
              alt={brokerage.name}
              className="w-full h-full object-contain p-6 transition-transform group-hover:scale-105"
              loading="lazy"
            />
          ) : (
            <Building2 className="h-16 w-16 text-muted-foreground" />
          )}

          {/* Compare checkbox overlay - top right like favorite button */}
          {onCompareToggle && brokerage.id && (
            <div
              className="absolute top-2 right-2 z-10 flex items-center gap-1.5 bg-background/80 hover:bg-background rounded-md px-2 py-1 backdrop-blur-sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onCompareToggle(brokerage.id!);
              }}
            >
              <Checkbox
                id={`compare-${brokerage.id}`}
                checked={isCompareSelected}
                disabled={compareDisabled && !isCompareSelected}
                className="h-4 w-4 pointer-events-none"
              />
              <label className="text-xs cursor-pointer">
                Compare
              </label>
            </div>
          )}

          {/* Sign-on bonus badge */}
          {brokerage.sign_on_bonus_max && brokerage.sign_on_bonus_max > 0 && (
            <div className="absolute bottom-2 left-2 z-10">
              <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-xs gap-1">
                <Gift className="h-3 w-3" />
                Up to {formatCurrency(brokerage.sign_on_bonus_max)} Bonus
              </Badge>
            </div>
          )}
        </div>

        {/* Content area - matching DealCard padding */}
        <div className="p-2 md:p-4 flex-1 flex flex-col">
          {/* Title */}
          <h3 className="font-bold text-sm md:text-lg group-hover:text-primary transition-colors line-clamp-1 mb-1">
            {brokerage.name}
          </h3>

          {/* Financial Snapshot - NEW */}
          {hasFinancialData && (
            <div className="grid grid-cols-3 gap-1.5 mb-2 md:mb-3 p-2 bg-muted/50 rounded-md">
              <div className="text-center">
                <p className="text-[10px] md:text-xs text-muted-foreground">Split</p>
                <p className="text-xs md:text-sm font-bold text-primary">
                  {formatSplit(brokerage.split_starting_percent, brokerage.split_max_percent)}
                </p>
              </div>
              <div className="text-center border-x border-border">
                <p className="text-[10px] md:text-xs text-muted-foreground">Cap</p>
                <p className="text-xs md:text-sm font-semibold">
                  {brokerage.cap_amount ? formatCurrency(brokerage.cap_amount) : 'None'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-[10px] md:text-xs text-muted-foreground">Monthly</p>
                <p className="text-xs md:text-sm font-semibold">
                  {brokerage.monthly_fee ? formatCurrency(brokerage.monthly_fee) : '$0'}
                </p>
              </div>
            </div>
          )}

          {/* Badges row */}
          <div className="flex flex-wrap gap-1.5 mb-2 md:mb-3">
            {brokerage.coverage_type && (
              <Badge variant="secondary" className={`text-xs capitalize ${getCoverageColor(brokerage.coverage_type)}`}>
                {brokerage.coverage_type}
              </Badge>
            )}
            {brokerage.brokerage_type && (
              <Badge variant="outline" className="text-xs gap-1">
                {getBrokerageTypeIcon(brokerage.brokerage_type)}
                {BROKERAGE_TYPE_LABELS[brokerage.brokerage_type] || brokerage.brokerage_type}
              </Badge>
            )}
            {brokerage.has_revenue_share && (
              <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-400">
                <TrendingUp className="h-3 w-3 mr-1" />
                Rev Share
              </Badge>
            )}
            {brokerage.has_stock_awards && (
              <Badge variant="secondary" className="text-xs bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-400">
                Stock
              </Badge>
            )}
          </div>

          {/* Best for tags */}
          {brokerage.best_for && brokerage.best_for.length > 0 && (
            <div className="mb-2 md:mb-3">
              <p className="text-xs text-muted-foreground mb-1">Best for:</p>
              <div className="flex flex-wrap gap-1">
                {brokerage.best_for.slice(0, 2).map((item) => (
                  <Badge key={item} variant="secondary" className="text-xs bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800">
                    <Check className="h-3 w-3 mr-1" />
                    {BEST_FOR_LABELS[item] || item}
                  </Badge>
                ))}
                {brokerage.best_for.length > 2 && (
                  <Badge variant="secondary" className="text-xs">+{brokerage.best_for.length - 2}</Badge>
                )}
              </div>
            </div>
          )}

          {/* Location info */}
          <div className="mt-auto space-y-1">
            {distance !== undefined && distance !== null && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <MapPin className="h-3 w-3" />
                <span>{Math.round(distance)} miles away</span>
              </div>
            )}

            {brokerage.locations_in_state !== undefined && brokerage.locations_in_state > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Users className="h-3 w-3" />
                <span>{brokerage.locations_in_state} {brokerage.locations_in_state === 1 ? 'office' : 'offices'}</span>
              </div>
            )}
          </div>

          {/* CTA Button */}
          <Button className="w-full mt-3" variant="default">
            View Details
          </Button>
        </div>
      </div>
    </Link>
  );
}
